/**
 * Wavefront OBJ parser (Faz 10.11) — text → triangle mesh payload for the
 * BIM 'Imported OBJ' element. Scope follows the OpenCADStudio parity note:
 * OBJ is import-only, mesh faces only (v/f plus vn for shading); materials,
 * curves and animation are ignored.
 *
 * Conventions:
 * - Axis conversion Y-up → Z-up uses the OCS formula (x, y, z) → (x, −z, y)
 *   so modeling-app meshes land upright in this Z-up scene.
 * - Faces of any vertex count are fan-triangulated (a quad splits into the
 *   two triangles (a,b,c) + (a,c,d) — the standard quad split); negative and
 *   v//vn style indices are supported.
 * - Degenerate (non-finite or repeated) vertices drop their triangle rather
 *   than poisoning the whole part.
 */

export interface ObjPart {
    /** o / g statement the part was opened with ('' when unnamed). */
    name: string;
    /** Flat xyz vertex positions, already converted to the Z-up frame. */
    positions: number[];
    /** Flat xyz vertex normals matching the position array length (if any). */
    normals: number[];
    /** Triangle indices into the position array (3 per face). */
    indices: number[];
}

export interface ObjParseResult {
    parts: ObjPart[];
    vertexCount: number;
    faceCount: number;
    /** Skipped lines with a short reason (parse kept going). */
    warnings: string[];
}

interface ObjVertex {
    x: number;
    y: number;
    z: number;
}

/** Convert an OBJ (Y-up) vertex to this app's Z-up frame: (x,y,z) → (x,−z,y). */
function toZUp(x: number, y: number, z: number): ObjVertex {
    return { x, y: -z, z: y };
}

function isFiniteVertex(v: ObjVertex): boolean {
    return Number.isFinite(v.x) && Number.isFinite(v.y) && Number.isFinite(v.z);
}

/**
 * Parse OBJ text into mesh parts. Throws only when the file holds no
 * geometry at all (callers decide how to report that to the user); per-line
 * problems are collected in `warnings` instead of aborting the import.
 */
export function parseObjText(text: string): ObjParseResult {
    const positions: ObjVertex[] = [];
    const normals: ObjVertex[] = [];
    const warnings: string[] = [];

    let current: ObjPart = { name: '', positions: [], normals: [], indices: [] };
    const parts: ObjPart[] = [];
    let faceCount = 0;

    const flushPart = () => {
        if (current.indices.length > 0) parts.push(current);
    };

    // Resolve an OBJ face index (1-based, negative = relative) to a flat
    // position index; returns -1 for out-of-range values.
    const resolve = (raw: string, count: number): number => {
        const n = Number(raw);
        if (!Number.isInteger(n)) return -1;
        const idx = n > 0 ? n - 1 : count + n;
        return idx >= 0 && idx < count ? idx : -1;
    };

    const lines = text.split(/\r?\n/);
    for (let lineNo = 0; lineNo < lines.length; lineNo++) {
        const line = lines[lineNo].trim();
        if (!line || line.startsWith('#')) continue;
        const parts2 = line.split(/\s+/);
        const keyword = parts2[0];

        if (keyword === 'v') {
            const v = toZUp(Number(parts2[1]), Number(parts2[2]), Number(parts2[3]));
            if (!isFiniteVertex(v)) {
                if (warnings.length < 50) warnings.push(`line ${lineNo + 1}: skipped non-finite vertex`);
                continue;
            }
            positions.push(v);
        } else if (keyword === 'vn') {
            const n: ObjVertex = { x: Number(parts2[1]), y: Number(parts2[2]), z: Number(parts2[3]) };
            if (isFiniteVertex(n)) normals.push(n);
        } else if (keyword === 'f') {
            if (parts2.length < 4) {
                if (warnings.length < 50) warnings.push(`line ${lineNo + 1}: face with fewer than 3 vertices skipped`);
                continue;
            }
            // Fan triangulation; a quad (4 corners) is the classic quad split
            // into (a,b,c) + (a,c,d).
            const cornerPos: number[] = [];
            const cornerNrm: number[] = [];
            let valid = true;
            for (let i = 1; i < parts2.length; i++) {
                const corner = parts2[i].split('/');
                const pi = resolve(corner[0], positions.length);
                if (pi < 0) {
                    if (warnings.length < 50) warnings.push(`line ${lineNo + 1}: face references unknown vertex ${corner[0]}`);
                    valid = false;
                    break;
                }
                cornerPos.push(pi);
                // vn index resolves against the normal array (may be absent).
                const niRaw = corner[2] ?? '';
                const ni = niRaw ? resolve(niRaw, normals.length) : -1;
                cornerNrm.push(ni);
            }
            if (!valid) continue;

            const pushTri = (a: number, b: number, c: number) => {
                // Drop degenerate triangles (repeated corner) defensively.
                if (a === b || b === c || a === c) return;
                const base = current.positions.length / 3;
                for (const pi of [a, b, c]) {
                    const v = positions[pi];
                    current.positions.push(v.x, v.y, v.z);
                }
                for (const ni of [cornerNrm[a], cornerNrm[b], cornerNrm[c]]) {
                    if (ni >= 0 && ni < normals.length) {
                        const n = normals[ni];
                        current.normals.push(n.x, n.y, n.z);
                    } else {
                        // Keep normals aligned with positions: pad missing ones.
                        current.normals.push(NaN, NaN, NaN);
                    }
                }
                current.indices.push(base, base + 1, base + 2);
                faceCount++;
            };
            for (let i = 1; i < cornerPos.length - 1; i++) {
                pushTri(cornerPos[0], cornerPos[i], cornerPos[i + 1]);
            }
        } else if (keyword === 'o' || keyword === 'g') {
            // Named group boundary: start a new part (empty parts are dropped
            // by flushPart, so consecutive o/g lines are harmless).
            flushPart();
            current = { name: parts2.slice(1).join(' '), positions: [], normals: [], indices: [] };
        }
        // vt / s / mtllib / usemtl / l and anything else: intentionally
        // ignored (import-only mesh scope).
    }
    flushPart();

    // Pad-missing normals produce NaN attributes — rebuild the normal array
    // without them so the renderer can computeVertexNormals as a fallback.
    for (const part of parts) {
        const hasNaN = part.normals.some((n) => Number.isNaN(n));
        if (hasNaN) part.normals = [];
    }

    const vertexCount = parts.reduce((sum, p) => sum + p.positions.length / 3, 0);
    if (parts.length === 0 || vertexCount === 0) {
        throw new Error('no mesh geometry found (v/f statements)');
    }
    return { parts, vertexCount, faceCount, warnings };
}
